import type { DailyEntryResponse } from "../schemas/daily-entry";
import type { WellnessMetricKey, WellnessRelationship, WellnessSummary } from "./types";

export function average(values: readonly (number | null)[]): number | null {
  const presentValues = values.filter((value): value is number => value !== null);

  if (presentValues.length === 0) {
    return null;
  }

  return Number(
    (presentValues.reduce((sum, value) => sum + value, 0) / presentValues.length).toFixed(1),
  );
}

export function compareMetricByMood(
  entries: readonly DailyEntryResponse[],
  key: WellnessMetricKey,
): WellnessRelationship {
  const pairedEntries = entries.filter((entry) => entry.mood !== null && entry[key] !== null);
  const highMoodEntries = pairedEntries.filter((entry) => (entry.mood ?? 0) >= 4);
  const lowMoodEntries = pairedEntries.filter((entry) => (entry.mood ?? 0) <= 3);

  return {
    highMoodAverage: average(highMoodEntries.map((entry) => entry[key])),
    lowMoodAverage: average(lowMoodEntries.map((entry) => entry[key])),
    days: pairedEntries.length,
  };
}

export function summarizeWellness(entries: readonly DailyEntryResponse[]): WellnessSummary {
  const moodEntries = entries.filter((entry) => entry.mood !== null);

  return {
    trackedMoodDays: moodEntries.length,
    averages: {
      mood: average(entries.map((entry) => entry.mood)),
      sleepHours: average(entries.map((entry) => entry.sleepHours)),
      sleepQuality: average(entries.map((entry) => entry.sleepQuality)),
      dietQuality: average(entries.map((entry) => entry.dietQuality)),
      exerciseQuality: average(entries.map((entry) => entry.exerciseQuality)),
    },
    relationships: {
      sleepHours: compareMetricByMood(entries, "sleepHours"),
      sleepQuality: compareMetricByMood(entries, "sleepQuality"),
      dietQuality: compareMetricByMood(entries, "dietQuality"),
      exerciseQuality: compareMetricByMood(entries, "exerciseQuality"),
    },
  };
}
